import React, { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Plus, Trash2 } from 'lucide-react';
import { useApi, apiGet, apiPost, apiDelete } from '../hooks/useApi';

const CATEGORIES = [
  { value: 'EA Purchase', color: '#58a6ff' },
  { value: 'VPS', color: '#a371f7' },
  { value: 'Broker Fee', color: '#f97583' },
  { value: 'Education', color: '#56d364' },
  { value: 'Software', color: '#d29922' },
  { value: 'Subscription', color: '#f0883e' },
  { value: 'Other', color: '#8b949e' },
];

const CATEGORY_COLORS = Object.fromEntries(CATEGORIES.map(c => [c.value, c.color]));

function formatCurrency(amount) {
  return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getToday() {
  return new Date().toISOString().split('T')[0];
}

export default function ExpenseTracker() {
  const [expenses, setExpenses] = useState([]);
  const { data: accounts } = useApi('/accounts');
  const [form, setForm] = useState({
    category: 'VPS',
    amount: '',
    description: '',
    date: getToday(),
    recurring: false,
    account: 'none',
  });
  const [showAddPopup, setShowAddPopup] = useState(false);

  // Load expenses from backend
  useEffect(() => {
    apiGet('/expenses').then(data => {
      if (Array.isArray(data)) setExpenses(data);
    }).catch(() => {});
  }, []);

  // Summary calculations
  const summary = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const totalExpenses = expenses.reduce((sum, e) => sum + Number(e.amount), 0);

    const thisMonth = expenses
      .filter(e => {
        const d = new Date(e.date);
        return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
      })
      .reduce((sum, e) => sum + Number(e.amount), 0);

    // Monthly average
    let monthlyAvg = 0;
    if (expenses.length > 0) {
      const dates = expenses.map(e => new Date(e.date));
      const earliest = new Date(Math.min(...dates));
      const monthsDiff = (currentYear - earliest.getFullYear()) * 12 + (currentMonth - earliest.getMonth()) + 1;
      monthlyAvg = totalExpenses / Math.max(monthsDiff, 1);
    }

    // ROI Impact - get total profit from localStorage if available
    let totalProfit = 0;
    try {
      const profitData = localStorage.getItem('total_profit');
      if (profitData) totalProfit = Number(profitData);
    } catch { /* ignore */ }
    const roiImpact = totalProfit > 0 ? (totalExpenses / totalProfit) * 100 : 0;

    return { totalExpenses, thisMonth, monthlyAvg, roiImpact };
  }, [expenses]);

  // Monthly breakdown for chart
  const monthlyData = useMemo(() => {
    const monthMap = {};
    expenses.forEach(e => {
      const d = new Date(e.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      if (!monthMap[key]) monthMap[key] = { month: label, total: 0, key };
      monthMap[key].total += Number(e.amount);
    });
    return Object.values(monthMap).sort((a, b) => a.key.localeCompare(b.key)).slice(-12);
  }, [expenses]);

  // Category breakdown
  const categoryData = useMemo(() => {
    const catMap = {};
    expenses.forEach(e => {
      if (!catMap[e.category]) catMap[e.category] = 0;
      catMap[e.category] += Number(e.amount);
    });
    const total = Object.values(catMap).reduce((s, v) => s + v, 0);
    return Object.entries(catMap)
      .map(([category, amount]) => ({
        category,
        amount,
        percentage: total > 0 ? (amount / total) * 100 : 0,
        color: CATEGORY_COLORS[category] || '#8b949e',
      }))
      .sort((a, b) => b.amount - a.amount);
  }, [expenses]);

  // Sorted expenses (newest first)
  const sortedExpenses = useMemo(() => {
    return [...expenses].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [expenses]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.amount || !form.description) return;

    try {
      const result = await apiPost('/expenses', {
        date: form.date,
        category: form.category,
        description: form.description,
        amount: parseFloat(form.amount),
        recurring: form.recurring,
      });

      const newExpense = {
        id: result.id,
        date: form.date,
        category: form.category,
        description: form.description,
        amount: parseFloat(form.amount),
        recurring: form.recurring,
      };

      setExpenses(prev => [...prev, newExpense]);
      setForm({ category: 'VPS', amount: '', description: '', date: getToday(), recurring: false, account: 'none' });
      setShowAddPopup(false);
    } catch (err) {
      console.error('Failed to add expense:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiDelete(`/expenses/${id}`);
      setExpenses(prev => prev.filter(e => e.id !== id));
    } catch (err) {
      console.error('Failed to delete expense:', err);
    }
  };

  return (
    <div className="space-y-6 animate-stagger">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-100">Expense Tracker</h1>
        <p className="text-gray-400 text-sm mt-1">Track your trading-related expenses</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="stat-card card-glow">
          <p className="text-gray-400 text-xs uppercase tracking-wide">Total Expenses</p>
          <p className="text-2xl font-bold text-accent-red mt-1">{formatCurrency(summary.totalExpenses)}</p>
          <p className="text-gray-500 text-xs mt-1">All time</p>
        </div>
        <div className="stat-card card-glow">
          <p className="text-gray-400 text-xs uppercase tracking-wide">This Month</p>
          <p className="text-2xl font-bold text-accent-orange mt-1">{formatCurrency(summary.thisMonth)}</p>
          <p className="text-gray-500 text-xs mt-1">Current month spending</p>
        </div>
        <div className="stat-card card-glow">
          <p className="text-gray-400 text-xs uppercase tracking-wide">Monthly Average</p>
          <p className="text-2xl font-bold text-accent-purple mt-1">{formatCurrency(summary.monthlyAvg)}</p>
          <p className="text-gray-500 text-xs mt-1">Avg per month</p>
        </div>
        <div className="stat-card card-glow">
          <p className="text-gray-400 text-xs uppercase tracking-wide">ROI Impact</p>
          <p className="text-2xl font-bold text-accent-green mt-1">{summary.roiImpact.toFixed(1)}%</p>
          <p className="text-gray-500 text-xs mt-1">Expenses vs profit</p>
        </div>
      </div>

      {/* Add Expense Button */}
      <button
        onClick={() => setShowAddPopup(true)}
        className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-accent-purple to-blue-500 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
      >
        <Plus size={16} /> Add Expense
      </button>

      {/* Add Expense Popup */}
      {showAddPopup && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center backdrop-blur-sm" onClick={() => setShowAddPopup(false)}>
          <div className="bg-[#0d1117] border border-[#21262d] rounded-2xl w-[95vw] max-w-[560px] max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 pb-4 border-b border-[#21262d]">
              <h2 className="text-white text-xl font-bold">Add Expense</h2>
              <button onClick={() => setShowAddPopup(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#21262d] text-gray-400 hover:text-white transition-colors">&times;</button>
            </div>

            <form onSubmit={handleAdd} className="p-6 space-y-5">
              {/* Name */}
              <div>
                <label className="text-gray-400 text-xs font-medium block mb-2">Name</label>
                <input type="text" placeholder="e.g., DigitalOcean VPS" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full bg-[#0d1117] border border-[#30363d] rounded-xl px-4 py-3.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#a855f7] transition-colors" required />
              </div>

              {/* Amount + Category */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 text-xs font-medium block mb-2">Amount</label>
                  <div className="flex">
                    <select className="w-16 flex-shrink-0 bg-[#0d1117] border border-[#30363d] border-r-0 rounded-l-xl px-2 py-3.5 text-sm text-gray-300 focus:outline-none">
                      <option>$</option>
                      <option>EUR</option>
                      <option>GBP</option>
                    </select>
                    <input type="number" step="0.01" min="0" placeholder="0.00" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} className="flex-1 min-w-0 bg-[#0d1117] border border-[#30363d] rounded-r-xl px-4 py-3.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#a855f7] transition-colors" required />
                  </div>
                </div>
                <div>
                  <label className="text-gray-400 text-xs font-medium block mb-2">Category</label>
                  <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full bg-[#0d1117] border border-[#30363d] rounded-xl px-4 py-3.5 text-sm text-white focus:outline-none focus:border-[#a855f7] transition-colors">
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.value}</option>)}
                  </select>
                </div>
              </div>

              {/* Date + Account */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 text-xs font-medium block mb-2">Date</label>
                  <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="w-full bg-[#0d1117] border border-[#30363d] rounded-xl px-4 py-3.5 text-sm text-white focus:outline-none focus:border-[#a855f7] transition-colors" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs font-medium block mb-2">Account (Optional)</label>
                  <select value={form.account} onChange={e => setForm({...form, account: e.target.value})} className="w-full bg-[#0d1117] border border-[#30363d] rounded-xl px-4 py-3.5 text-sm text-white focus:outline-none focus:border-[#a855f7] transition-colors">
                    <option value="none">None (General)</option>
                    {(accounts || []).map(a => (
                      <option key={a.id} value={a.id}>{a.broker ? `${a.broker} - ` : ''}#{a.account_number}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Recurring Toggle */}
              <div className="flex items-center gap-3 py-1">
                <button type="button" onClick={() => setForm({...form, recurring: !form.recurring})} className={`w-12 h-6 rounded-full transition-all duration-200 ${form.recurring ? 'bg-[#a855f7]' : 'bg-[#30363d]'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200 ${form.recurring ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
                <span className="text-gray-200 text-sm font-medium">Recurring expense</span>
                {form.recurring && <span className="text-[10px] px-2 py-0.5 rounded bg-[#a855f7]/20 text-[#a855f7] font-medium">Monthly</span>}
              </div>

              {/* Frequency (shown when recurring) */}
              {form.recurring && (
                <div>
                  <label className="text-gray-400 text-xs font-medium block mb-2">Frequency</label>
                  <select value={form.frequency || 'monthly'} onChange={e => setForm({...form, frequency: e.target.value})} className="w-full bg-[#0d1117] border border-[#30363d] rounded-xl px-4 py-3.5 text-sm text-white focus:outline-none focus:border-[#a855f7] transition-colors">
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
              )}

              {/* Description */}
              <div>
                <label className="text-gray-400 text-xs font-medium block mb-2">Description (Optional)</label>
                <textarea placeholder="Additional notes..." rows={3} value={form.notes || ''} onChange={e => setForm({...form, notes: e.target.value})} className="w-full bg-[#0d1117] border border-[#30363d] rounded-xl px-4 py-3.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#a855f7] transition-colors resize-none" />
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-3 border-t border-[#21262d]">
                <button type="button" onClick={() => setShowAddPopup(false)} className="px-6 py-2.5 bg-[#161b22] border border-[#30363d] rounded-xl text-sm text-gray-300 font-medium hover:text-white hover:border-gray-500 transition-colors">Cancel</button>
                <button type="submit" className="px-6 py-2.5 bg-gradient-to-r from-[#a855f7] to-[#6366f1] text-white rounded-xl text-sm font-bold hover:opacity-90 transition-opacity shadow-lg shadow-purple-500/20">Add Expense</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Monthly Breakdown Chart */}
        <div className="card lg:col-span-2">
          <h2 className="text-white font-semibold mb-4">Monthly Breakdown</h2>
          {monthlyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
                <XAxis dataKey="month" stroke="#8b949e" fontSize={12} />
                <YAxis stroke="#8b949e" fontSize={12} tickFormatter={v => `$${v}`} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#161b22', border: '1px solid #21262d', borderRadius: '8px' }}
                  labelStyle={{ color: '#e6edf3' }}
                  formatter={(value) => [formatCurrency(value), 'Expenses']}
                />
                <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                  {monthlyData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill="#f97583" fillOpacity={0.8} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[250px] text-gray-500 text-sm">
              No expense data yet. Add your first expense above.
            </div>
          )}
        </div>

        {/* Category Breakdown */}
        <div className="card">
          <h2 className="text-white font-semibold mb-4">By Category</h2>
          {categoryData.length > 0 ? (
            <div className="space-y-3">
              {categoryData.map(cat => (
                <div key={cat.category}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-300">{cat.category}</span>
                    <span className="text-gray-400">{formatCurrency(cat.amount)} ({cat.percentage.toFixed(1)}%)</span>
                  </div>
                  <div className="w-full bg-dark-bg rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{ width: `${cat.percentage}%`, backgroundColor: cat.color }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-[200px] text-gray-500 text-sm">
              No categories yet
            </div>
          )}
        </div>
      </div>

      {/* Expense List */}
      <div className="card">
        <h2 className="text-white font-semibold mb-4">All Expenses</h2>
        {sortedExpenses.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-border">
                  <th className="text-left text-gray-400 font-medium py-2 px-3">Date</th>
                  <th className="text-left text-gray-400 font-medium py-2 px-3">Category</th>
                  <th className="text-left text-gray-400 font-medium py-2 px-3">Description</th>
                  <th className="text-right text-gray-400 font-medium py-2 px-3">Amount</th>
                  <th className="text-center text-gray-400 font-medium py-2 px-3">Recurring</th>
                  <th className="text-center text-gray-400 font-medium py-2 px-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedExpenses.map(expense => (
                  <tr key={expense.id} className="border-b border-dark-border/50 hover:bg-dark-hover/50 transition-colors">
                    <td className="py-2.5 px-3 text-gray-300">
                      {new Date(expense.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td className="py-2.5 px-3">
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                        style={{
                          backgroundColor: `${CATEGORY_COLORS[expense.category]}20`,
                          color: CATEGORY_COLORS[expense.category],
                          border: `1px solid ${CATEGORY_COLORS[expense.category]}40`,
                        }}
                      >
                        {expense.category}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-gray-300">{expense.description}</td>
                    <td className="py-2.5 px-3 text-right text-accent-red font-medium">
                      {formatCurrency(expense.amount)}
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      {expense.recurring && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-accent-purple/10 text-accent-purple border border-accent-purple/30">
                          Monthly
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <button
                        onClick={() => handleDelete(expense.id)}
                        className="text-gray-500 hover:text-accent-red transition-colors p-1 rounded hover:bg-accent-red/10"
                        title="Delete expense"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg mb-1">No expenses recorded</p>
            <p className="text-sm">Start tracking your trading costs using the form above</p>
          </div>
        )}
      </div>
    </div>
  );
}
